/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/coalesce.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Returns the first of its arguments that is not null. If all arguments are null, it returns `null`.
 *
 * @example
 * ROW a=null, b="b"
 * | EVAL COALESCE(a, b)
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'coalesce',
  signatures: [
    {
      params: [
        {
          name: 'first',
          type: 'boolean',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'first',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'rest',
          type: 'boolean',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'first',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'rest',
          type: 'cartesian_point',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'first',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'rest',
          type: 'cartesian_shape',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'first',
          type: 'date',
          optional: false,
        },
        {
          name: 'rest',
          type: 'date',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'first',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'rest',
          type: 'date_nanos',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'first',
          type: 'date_range',
          optional: false,
        },
        {
          name: 'rest',
          type: 'date_range',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'date_range',
    },
    {
      params: [
        {
          name: 'first',
          type: 'dense_vector',
          optional: false,
        },
        {
          name: 'rest',
          type: 'dense_vector',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'dense_vector',
    },
    {
      params: [
        {
          name: 'first',
          type: 'double_range',
          optional: false,
        },
        {
          name: 'rest',
          type: 'double_range',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'double_range',
    },
    {
      params: [
        {
          name: 'first',
          type: 'exponential_histogram',
          optional: false,
        },
        {
          name: 'rest',
          type: 'exponential_histogram',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'exponential_histogram',
    },
    {
      params: [
        {
          name: 'first',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'rest',
          type: 'flattened',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'flattened',
    },
    {
      params: [
        {
          name: 'first',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'rest',
          type: 'geo_point',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'first',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'rest',
          type: 'geo_shape',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'first',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'rest',
          type: 'geohash',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geohash',
    },
    {
      params: [
        {
          name: 'first',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'rest',
          type: 'geohex',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geohex',
    },
    {
      params: [
        {
          name: 'first',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'rest',
          type: 'geotile',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'geotile',
    },
    {
      params: [
        {
          name: 'first',
          type: 'histogram',
          optional: false,
        },
        {
          name: 'rest',
          type: 'histogram',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'histogram',
    },
    {
      params: [
        {
          name: 'first',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'first',
          type: 'integer',
          optional: false,
        },
        {
          name: 'rest',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'first',
          type: 'ip',
          optional: false,
        },
        {
          name: 'rest',
          type: 'ip',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'first',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'first',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'rest',
          type: 'keyword',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'first',
          type: 'long',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'first',
          type: 'long',
          optional: false,
        },
        {
          name: 'rest',
          type: 'long',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'first',
          type: 'tdigest',
          optional: false,
        },
        {
          name: 'rest',
          type: 'tdigest',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'tdigest',
    },
    {
      params: [
        {
          name: 'first',
          type: 'text',
          optional: false,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'first',
          type: 'text',
          optional: false,
        },
        {
          name: 'rest',
          type: 'text',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'first',
          type: 'version',
          optional: false,
        },
        {
          name: 'rest',
          type: 'version',
          optional: true,
        },
      ],
      variadic: true,
      returnType: 'version',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
